import * as XLSX from "xlsx";

const formatExportDate = (dateVal) => {
  if (!dateVal) return "-";
  const d = new Date(dateVal);
  if (Number.isNaN(d.getTime())) return String(dateVal);
  return d.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatSimpleDate = (dateVal) => {
  if (!dateVal) return "-";
  const d = new Date(dateVal);
  if (Number.isNaN(d.getTime())) return String(dateVal);
  return d.toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export async function exportReportToExcel({
  activeReport,
  reportLabel,
  rows = [],
  profitLossData = null,
  dailyTotals = [],
  startDate,
  endDate,
}) {
  const safeRows = Array.isArray(rows) ? rows : [];
  let headers = [];
  let dataRows = [];
  let merges = [];
  let colWidths = [];

  const getDateKey = (r) => {
    if (!r?.date) return "";
    const d = new Date(r.date);
    if (Number.isNaN(d.getTime())) return String(r.date).slice(0, 10);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  const getNotaKey = (r) => {
    if (r?.order_id) return String(r.order_id);
    if (r?.transaction_number) return String(r.transaction_number);
    if (r?.order_number) return String(r.order_number);
    return `${getDateKey(r)}_${r?.total_per_nota}_${r?.receiver}`;
  };

  const dailyTotalMap = {};
  if (Array.isArray(dailyTotals)) {
    for (const item of dailyTotals) {
      if (!item?.date) continue;
      const d = new Date(item.date);
      if (Number.isNaN(d.getTime())) continue;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      dailyTotalMap[key] = Number(item.total_per_day || 0);
    }
  }

  const headerOffset = 1; // Row 0 is header

  if (activeReport === "sales") {
    headers = [
      "No",
      "Tanggal",
      "Nama Produk",
      "Jumlah",
      "Harga",
      "Sub Total",
      "Total / Nota",
      "Unit",
      "Penerima",
      "Total / Hari",
    ];

    let i = 0;
    let rowNumber = 1;
    let totalQty = 0;
    let totalSubtotal = 0;

    while (i < safeRows.length) {
      const currentDateKey = getDateKey(safeRows[i]);
      let dateEnd = i;
      while (
        dateEnd < safeRows.length &&
        getDateKey(safeRows[dateEnd]) === currentDateKey
      ) {
        dateEnd++;
      }

      const rowDayTotal = Number(safeRows[i]?.total_per_day || 0);
      let dayTotal = rowDayTotal > 0 ? rowDayTotal : dailyTotalMap[currentDateKey];
      if (dayTotal === undefined || dayTotal === null || dayTotal === 0) {
        const seenNotas = new Set();
        dayTotal = 0;
        for (let k = i; k < dateEnd; k++) {
          const nKey = getNotaKey(safeRows[k]);
          if (!seenNotas.has(nKey)) {
            seenNotas.add(nKey);
            dayTotal += Number(safeRows[k]?.total_per_nota || 0);
          }
        }
      }

      const dateStartRow = headerOffset + i;
      const dateEndRow = headerOffset + dateEnd - 1;
      if (dateEnd - i > 1) {
        merges.push({ s: { r: dateStartRow, c: 1 }, e: { r: dateEndRow, c: 1 } });
        merges.push({ s: { r: dateStartRow, c: 9 }, e: { r: dateEndRow, c: 9 } });
      }

      let k = i;
      while (k < dateEnd) {
        const currentNotaKey = getNotaKey(safeRows[k]);
        let notaEnd = k;
        while (
          notaEnd < dateEnd &&
          getNotaKey(safeRows[notaEnd]) === currentNotaKey
        ) {
          notaEnd++;
        }

        const notaStartRow = headerOffset + k;
        const notaEndRow = headerOffset + notaEnd - 1;
        if (notaEnd - k > 1) {
          merges.push({ s: { r: notaStartRow, c: 6 }, e: { r: notaEndRow, c: 6 } });
          merges.push({ s: { r: notaStartRow, c: 8 }, e: { r: notaEndRow, c: 8 } });
        }

        for (let m = k; m < notaEnd; m++) {
          const r = safeRows[m];
          const qty = Number(r.quantity || 0);
          const price = Number(r.price || 0);
          const sub = Number(r.sub_total || 0);
          const notaTotal = Number(r.total_per_nota || 0);

          totalQty += qty;
          totalSubtotal += sub;

          dataRows.push([
            rowNumber++,
            formatSimpleDate(r.date),
            r.product_name || "-",
            qty,
            price,
            sub,
            notaTotal,
            r.unit || r.unit_name || "-",
            r.receiver && String(r.receiver).trim() && String(r.receiver).trim() !== "-"
              ? String(r.receiver).trim()
              : (r.customer_name || r.customer || "Pelanggan Umum"),
            dayTotal,
          ]);
        }
        k = notaEnd;
      }
      i = dateEnd;
    }

    const summaryRowIdx = headerOffset + dataRows.length;
    dataRows.push([
      "TOTAL",
      "",
      "",
      totalQty,
      "",
      totalSubtotal,
      "",
      "",
      "",
      "",
    ]);
    merges.push({ s: { r: summaryRowIdx, c: 0 }, e: { r: summaryRowIdx, c: 2 } });

    colWidths = [
      { wch: 6 },
      { wch: 14 },
      { wch: 32 },
      { wch: 10 },
      { wch: 15 },
      { wch: 16 },
      { wch: 16 },
      { wch: 18 },
      { wch: 24 },
      { wch: 16 },
    ];
  } else if (activeReport === "recap-units") {
    headers = [
      "No",
      "Nama Unit",
      "Sparepart",
      "Jumlah",
      "Satuan",
      "Sub Total",
      "Total Pemakaian",
    ];

    let i = 0;
    let groupNo = 1;
    let totalQty = 0;
    let totalSales = 0;

    while (i < safeRows.length) {
      const currentUnit = String(safeRows[i]?.unit || safeRows[i]?.unit_name || "-");
      let unitEnd = i;
      let unitSum = 0;
      while (
        unitEnd < safeRows.length &&
        String(safeRows[unitEnd]?.unit || safeRows[unitEnd]?.unit_name || "-") === currentUnit
      ) {
        unitSum += Number(safeRows[unitEnd]?.sub_total || safeRows[unitEnd]?.total_sales || 0);
        unitEnd++;
      }

      const unitStartRow = headerOffset + i;
      const unitEndRow = headerOffset + unitEnd - 1;
      if (unitEnd - i > 1) {
        merges.push({ s: { r: unitStartRow, c: 0 }, e: { r: unitEndRow, c: 0 } });
        merges.push({ s: { r: unitStartRow, c: 1 }, e: { r: unitEndRow, c: 1 } });
        merges.push({ s: { r: unitStartRow, c: 6 }, e: { r: unitEndRow, c: 6 } });
      }

      for (let m = i; m < unitEnd; m++) {
        const r = safeRows[m];
        const qty = Number(r.quantity || r.total_items || 0);
        const sub = Number(r.sub_total || r.total_sales || 0);
        totalQty += qty;
        totalSales += sub;

        dataRows.push([
          groupNo,
          currentUnit,
          r.sparepart || r.product_name || "-",
          qty,
          r.satuan || "-",
          sub,
          unitSum,
        ]);
      }
      groupNo++;
      i = unitEnd;
    }

    const summaryRowIdx = headerOffset + dataRows.length;
    dataRows.push([
      "TOTAL",
      "",
      "",
      totalQty,
      "",
      totalSales,
      totalSales,
    ]);
    merges.push({ s: { r: summaryRowIdx, c: 0 }, e: { r: summaryRowIdx, c: 2 } });

    colWidths = [
      { wch: 6 },
      { wch: 22 },
      { wch: 32 },
      { wch: 10 },
      { wch: 12 },
      { wch: 16 },
      { wch: 18 },
    ];
  } else if (activeReport === "transaction-history") {
    headers = [
      "No",
      "Tanggal & Waktu",
      "No. Transaksi",
      "Pelanggan",
      "Sumber",
      "Tipe Penjualan",
      "Subtotal",
      "Biaya Lain",
      "Grand Total",
    ];

    let sumSubtotal = 0;
    let sumOther = 0;
    let sumGrand = 0;

    safeRows.forEach((r, idx) => {
      const sub = Number(r.sub_total || 0);
      const other = Number(r.other_cost || 0);
      const grand = Number(r.grand_total || 0);
      sumSubtotal += sub;
      sumOther += other;
      sumGrand += grand;

      dataRows.push([
        idx + 1,
        formatExportDate(r.date),
        r.transaction_no || r.transaction_number || "-",
        r.customer || "Umum",
        r.source || "-",
        r.type || "-",
        sub,
        other,
        grand,
      ]);
    });

    const summaryRowIdx = headerOffset + dataRows.length;
    dataRows.push([
      "TOTAL",
      "",
      "",
      "",
      "",
      "",
      sumSubtotal,
      sumOther,
      sumGrand,
    ]);
    merges.push({ s: { r: summaryRowIdx, c: 0 }, e: { r: summaryRowIdx, c: 5 } });

    colWidths = [
      { wch: 6 },
      { wch: 20 },
      { wch: 22 },
      { wch: 20 },
      { wch: 14 },
      { wch: 16 },
      { wch: 16 },
      { wch: 14 },
      { wch: 16 },
    ];
  } else if (activeReport === "shifts") {
    headers = [
      "No",
      "No. Shift",
      "Kasir",
      "Waktu Buka",
      "Waktu Tutup",
      "Kas Awal",
      "Estimasi Kas Fisik",
      "Kas Aktual",
      "Selisih Kas",
      "Penjualan Tunai",
      "Penjualan Non-Tunai",
      "Status",
    ];

    let sumOpenCash = 0;
    let sumExpCash = 0;
    let sumActCash = 0;
    let sumDiff = 0;
    let sumCashSales = 0;
    let sumNonCashSales = 0;

    safeRows.forEach((r, idx) => {
      const openCash = Number(r.opening_cash || 0);
      const expCash = Number(r.expected_cash || 0);
      const actCash = Number(r.actual_cash || 0);
      const diff = Number(r.cash_difference || 0);
      const cashSales = Number(r.total_cash_sales || 0);
      const nonCashSales = Number(r.total_non_cash_sales || 0);

      sumOpenCash += openCash;
      sumExpCash += expCash;
      sumActCash += actCash;
      sumDiff += diff;
      sumCashSales += cashSales;
      sumNonCashSales += nonCashSales;

      dataRows.push([
        idx + 1,
        r.shift_number || "-",
        r.opened_by_name || "-",
        formatExportDate(r.opened_at),
        r.closed_at ? formatExportDate(r.closed_at) : "-",
        openCash,
        expCash,
        actCash,
        diff,
        cashSales,
        nonCashSales,
        r.status === "CLOSED" ? "Ditutup" : "Aktif",
      ]);
    });

    const summaryRowIdx = headerOffset + dataRows.length;
    dataRows.push([
      "TOTAL",
      "",
      "",
      "",
      "",
      sumOpenCash,
      sumExpCash,
      sumActCash,
      sumDiff,
      sumCashSales,
      sumNonCashSales,
      "",
    ]);
    merges.push({ s: { r: summaryRowIdx, c: 0 }, e: { r: summaryRowIdx, c: 4 } });

    colWidths = [
      { wch: 6 },
      { wch: 18 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 },
      { wch: 15 },
      { wch: 18 },
      { wch: 15 },
      { wch: 15 },
      { wch: 16 },
      { wch: 18 },
      { wch: 12 },
    ];
  } else if (activeReport === "profit-loss") {
    headers = ["Uraian", "Jumlah (Rp)", "% Terhadap Pendapatan"];
    const d = profitLossData || {};
    const rev = Number(d.pendapatan || 0);
    const calcPct = (v) => {
      if (!rev) return "0,00%";
      return (
        ((Number(v || 0) / rev) * 100).toLocaleString("id-ID", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }) + "%"
      );
    };

    dataRows = [
      ["Pendapatan", Number(d.pendapatan || 0), "100,00%"],
      ["Diskon", -Number(d.diskon || 0), `-${calcPct(d.diskon)}`],
      ["Pajak", -Number(d.pajak || 0), `-${calcPct(d.pajak)}`],
      ["Pendapatan Bersih", Number(d.pendapatan_bersih || 0), calcPct(d.pendapatan_bersih)],
      ["Total HPP", -Number(d.hpp || 0), `-${calcPct(d.hpp)}`],
      ["Biaya Operasional", -Number(d.biaya_operasional || 0), `-${calcPct(d.biaya_operasional)}`],
      ["Total Biaya", -Number(d.total_biaya || 0), `-${calcPct(d.total_biaya)}`],
      ["Laba Bersih", Number(d.laba_bersih || 0), calcPct(d.laba_bersih)],
    ];

    colWidths = [{ wch: 28 }, { wch: 20 }, { wch: 24 }];
  } else if (activeReport === "recap-products") {
    headers = [
      "No",
      "Produk",
      "Tipe Item",
      "Qty",
      "Satuan",
      "Subtotal Penjualan",
    ];

    let sumQty = 0;
    let sumSales = 0;

    safeRows.forEach((r, idx) => {
      let displayName = String(r.product_name || r.name || "-").trim();
      if (r.product_addon_id) {
        const parts = displayName.split(" - ");
        if (parts.length > 1) {
          displayName = `+ ${parts[parts.length - 1]}`;
        }
      } else {
        const detailParts = [];
        if (r.brand_name) detailParts.push(`Merk: ${r.brand_name}`);
        if (r.variant_name) detailParts.push(`Varian: ${r.variant_name}`);
        if (detailParts.length > 0) {
          displayName += ` (${detailParts.join(" | ")})`;
        }
      }

      const qty = Number(r.quantity || r.total_quantity || r.total_items || 0);
      const sales = Number(r.total_sales || r.sub_total || r.total_price || r.gross_sales || r.net_sales || 0);
      const satuan = r.product_addon_id ? "-" : (r.unit || r.satuan || "-");
      const itemType = r.item_type || "PRODUCT";

      sumQty += qty;
      sumSales += sales;

      dataRows.push([
        idx + 1,
        displayName,
        itemType,
        qty,
        satuan,
        sales,
      ]);
    });

    const summaryRowIdx = headerOffset + dataRows.length;
    dataRows.push([
      "TOTAL",
      "",
      "",
      sumQty,
      "",
      sumSales,
    ]);
    merges.push({ s: { r: summaryRowIdx, c: 0 }, e: { r: summaryRowIdx, c: 2 } });

    colWidths = [
      { wch: 6 },
      { wch: 32 },
      { wch: 14 },
      { wch: 10 },
      { wch: 12 },
      { wch: 20 },
    ];
  } else if (activeReport === "recap-categories") {
    headers = [
      "No",
      "Kategori",
      "Jumlah Transaksi",
      "Subtotal Penjualan",
      "Kontribusi (%)",
    ];

    let sumTx = 0;
    let sumSales = 0;

    safeRows.forEach((r) => {
      sumTx += Number(r.transaction_count || 0);
      sumSales += Number(r.total_sales || r.net_sales || r.gross_sales || 0);
    });

    safeRows.forEach((r, idx) => {
      const tx = Number(r.transaction_count || 0);
      const sales = Number(r.total_sales || r.net_sales || r.gross_sales || 0);
      const pct = r.percentage
        ? `${Number(r.percentage).toFixed(2)}%`
        : sumSales > 0
        ? `${((sales / sumSales) * 100).toFixed(2)}%`
        : "0,00%";

      dataRows.push([
        idx + 1,
        r.name || r.category_name || "-",
        tx,
        sales,
        pct,
      ]);
    });

    const summaryRowIdx = headerOffset + dataRows.length;
    dataRows.push([
      "TOTAL",
      "",
      sumTx,
      sumSales,
      "100,00%",
    ]);
    merges.push({ s: { r: summaryRowIdx, c: 0 }, e: { r: summaryRowIdx, c: 1 } });

    colWidths = [
      { wch: 6 },
      { wch: 28 },
      { wch: 18 },
      { wch: 22 },
      { wch: 16 },
    ];
  } else if (activeReport === "recap-payments") {
    headers = [
      "No",
      "Metode Pembayaran",
      "Jumlah Transaksi",
      "Total Nominal",
      "Persentase (%)",
    ];

    let sumTx = 0;
    let sumAmount = 0;

    safeRows.forEach((r) => {
      sumTx += Number(r.transaction_count || 0);
      sumAmount += Number(r.total_sales || r.total_amount || 0);
    });

    safeRows.forEach((r, idx) => {
      const tx = Number(r.transaction_count || 0);
      const amt = Number(r.total_sales || r.total_amount || 0);
      const pct = r.percentage
        ? `${Number(r.percentage).toFixed(2)}%`
        : sumAmount > 0
        ? `${((amt / sumAmount) * 100).toFixed(2)}%`
        : "0,00%";

      dataRows.push([
        idx + 1,
        r.payment_method || r.name || "-",
        tx,
        amt,
        pct,
      ]);
    });

    const summaryRowIdx = headerOffset + dataRows.length;
    dataRows.push([
      "TOTAL",
      "",
      sumTx,
      sumAmount,
      "100,00%",
    ]);
    merges.push({ s: { r: summaryRowIdx, c: 0 }, e: { r: summaryRowIdx, c: 1 } });

    colWidths = [
      { wch: 6 },
      { wch: 25 },
      { wch: 18 },
      { wch: 20 },
      { wch: 16 },
    ];
  } else if (activeReport === "recap-sales-types") {
    headers = [
      "No",
      "Tipe Penjualan",
      "Jumlah Transaksi",
      "Total Nominal",
      "Persentase (%)",
    ];

    let sumTx = 0;
    let sumAmount = 0;

    safeRows.forEach((r) => {
      sumTx += Number(r.transaction_count || 0);
      sumAmount += Number(r.total_sales || r.total_amount || 0);
    });

    safeRows.forEach((r, idx) => {
      const tx = Number(r.transaction_count || 0);
      const amt = Number(r.total_sales || r.total_amount || 0);
      const pct = r.percentage
        ? `${Number(r.percentage).toFixed(2)}%`
        : sumAmount > 0
        ? `${((amt / sumAmount) * 100).toFixed(2)}%`
        : "0,00%";

      dataRows.push([
        idx + 1,
        r.order_type || r.name || "-",
        tx,
        amt,
        pct,
      ]);
    });

    const summaryRowIdx = headerOffset + dataRows.length;
    dataRows.push([
      "TOTAL",
      "",
      sumTx,
      sumAmount,
      "100,00%",
    ]);
    merges.push({ s: { r: summaryRowIdx, c: 0 }, e: { r: summaryRowIdx, c: 1 } });

    colWidths = [
      { wch: 6 },
      { wch: 25 },
      { wch: 18 },
      { wch: 20 },
      { wch: 16 },
    ];
  } else if (activeReport === "void-transactions") {
    headers = [
      "No",
      "Tanggal",
      "No. Transaksi",
      "Pelanggan",
      "Tipe",
      "Sub Total",
      "Biaya Lain",
      "Grand Total",
      "Metode Bayar",
      "Dibuat Oleh",
      "Dibatalkan Oleh",
      "Tanggal Batal",
      "Alasan Void",
    ];

    let sumSubTotal = 0;
    let sumOtherCost = 0;
    let sumGrandTotal = 0;

    safeRows.forEach((r, idx) => {
      const subTotal = Number(r.sub_total || 0);
      const otherCost = Number(r.other_cost || 0);
      const grandTotal = Number(r.grand_total || r.total_price || (subTotal + otherCost));

      sumSubTotal += subTotal;
      sumOtherCost += otherCost;
      sumGrandTotal += grandTotal;

      dataRows.push([
        idx + 1,
        formatExportDate(r.date || r.created_at),
        r.transaction_no || r.order_number || r.transaction_number || "-",
        r.customer || "-",
        r.type || "-",
        subTotal,
        otherCost,
        grandTotal,
        r.payment_method || "-",
        r.created_by || r.created_by_name || "-",
        r.canceled_by || r.voided_by_name || "-",
        r.canceled_at ? formatExportDate(r.canceled_at) : "-",
        r.void_description || r.reason || "-",
      ]);
    });

    const summaryRowIdx = headerOffset + dataRows.length;
    dataRows.push([
      "TOTAL",
      "",
      "",
      "",
      "",
      sumSubTotal,
      sumOtherCost,
      sumGrandTotal,
      "",
      "",
      "",
      "",
      "",
    ]);
    merges.push({ s: { r: summaryRowIdx, c: 0 }, e: { r: summaryRowIdx, c: 4 } });

    colWidths = [
      { wch: 6 },
      { wch: 18 },
      { wch: 22 },
      { wch: 18 },
      { wch: 14 },
      { wch: 16 },
      { wch: 14 },
      { wch: 16 },
      { wch: 15 },
      { wch: 18 },
      { wch: 18 },
      { wch: 18 },
      { wch: 28 },
    ];
  } else {
    headers = ["No", "Data"];
    safeRows.forEach((r, idx) => {
      dataRows.push([idx + 1, JSON.stringify(r)]);
    });
  }

  const aoa = [headers, ...dataRows];
  const worksheet = XLSX.utils.aoa_to_sheet(aoa);

  if (merges.length > 0) {
    worksheet["!merges"] = merges;
  }
  if (colWidths.length > 0) {
    worksheet["!cols"] = colWidths;
  }

  const workbook = XLSX.utils.book_new();
  const safeSheetName =
    (reportLabel || "Laporan")
      .replace(/[:\\/?*\[\]]/g, " ")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, 31) || "Laporan";
  XLSX.utils.book_append_sheet(workbook, worksheet, safeSheetName);

  const cleanName = (reportLabel || "Laporan").replace(/[^a-zA-Z0-9]/g, "_");
  const fileName = `${cleanName}_${startDate}_sd_${endDate}.xlsx`;

  try {
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      if (document.body.contains(link)) {
        document.body.removeChild(link);
      }
      window.URL.revokeObjectURL(url);
    }, 500);
  } catch (writeErr) {
    console.warn("Blob download fallback to XLSX.writeFile:", writeErr);
    XLSX.writeFile(workbook, fileName);
  }
}
